(() => {
  'use strict';
  if (customElements.get('italflex-catalog-reader')) return;

  class ItalFlexCatalogReader extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      let manifest;
      try { manifest = JSON.parse(this.querySelector('[data-catalog-manifest]').textContent); }
      catch (_) { return; }
      if (!Array.isArray(manifest.pages) || !manifest.pages.length) return;
      this.pages = manifest.pages.filter(page => page && typeof page.src === 'string' && page.src);
      if (!this.pages.length) return;
      this.ready = true;
      this.index = 0;
      this.preloaded = new Set();
      this.listeners = [];
      this.image = this.querySelector('[data-page-image]');
      this.stage = this.querySelector('[data-stage]');
      this.select = this.querySelector('[data-page-select]');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.contents = this.querySelector('[data-contents]');
      this.thumbnails = this.querySelector('[data-thumbnails]');
      this.dialog = this.querySelector('[data-zoom-dialog]');
      this.zoomImage = this.querySelector('[data-zoom-image]');
      this.zoomPan = this.querySelector('[data-zoom-pan]');
      this.fullscreenButton = this.querySelector('[data-fullscreen]');
      this.on = (node, type, handler) => {
        node.addEventListener(type, handler);
        this.listeners.push({ node, type, handler });
      };
      this.select.textContent = '';
      this.thumbnails.textContent = '';
      this.contentsBuilt = false;

      const pdfUrl = this.dataset.pdfUrl || manifest.pdfUrl;
      const download = this.querySelector('[data-download]');
      if (pdfUrl) { download.href = pdfUrl; download.hidden = false; }
      this.pages.forEach((page, index) => {
        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = `${index + 1}. ${page.title || 'Pagina ' + (index + 1)}`;
        this.select.append(option);
      });

      this.on(this.previous, 'click', () => this.setPage(this.index - 1, true));
      this.on(this.next, 'click', () => this.setPage(this.index + 1, true));
      this.on(this.select, 'change', () => this.setPage(Number(this.select.value), true));
      this.on(this.querySelector('[data-zoom]'), 'click', event => this.openZoom(event.currentTarget));
      this.on(this.querySelector('[data-open-page]'), 'click', event => {
        if (Date.now() < (this.suppressClickUntil || 0)) return;
        this.openZoom(event.currentTarget);
      });
      this.on(this.querySelector('[data-zoom-close]'), 'click', () => this.closeZoom());
      this.on(this.querySelector('[data-zoom-in]'), 'click', () => this.setZoom(this.zoom + .5));
      this.on(this.querySelector('[data-zoom-out]'), 'click', () => this.setZoom(this.zoom - .5));
      this.on(this.dialog, 'cancel', event => { event.preventDefault(); this.closeZoom(); });
      this.on(this.fullscreenButton, 'click', () => this.toggleFullscreen());
      this.on(document, 'fullscreenchange', () => this.updateFullscreen());
      this.on(window, 'hashchange', () => this.readHash());
      this.on(window, 'popstate', () => this.readHash());
      this.on(this, 'keydown', event => this.onKeyDown(event));
      this.on(this.contents, 'toggle', () => {
        if (!this.contents.open) return;
        this.buildContents();
        const selected = this.thumbnails.querySelector('[aria-current="page"]');
        if (selected) selected.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
      this.on(this.querySelector('[data-retry]'), 'click', () => this.renderPage(true));
      this.on(this.stage, 'pointerdown', event => {
        if (!event.isPrimary || this.dialog.hasAttribute('open') || event.button > 0) return;
        this.pointer = { x: event.clientX, y: event.clientY, id: event.pointerId };
      });
      this.on(this.stage, 'pointercancel', () => { this.pointer = null; });
      this.on(this.stage, 'pointerup', event => {
        if (!this.pointer || this.pointer.id !== event.pointerId || this.dialog.hasAttribute('open')) return;
        const dx = event.clientX - this.pointer.x;
        const dy = event.clientY - this.pointer.y;
        this.pointer = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.35) return;
        this.suppressClickUntil = Date.now() + 600;
        this.setPage(this.index + (dx < 0 ? 1 : -1), true);
      });
      this.on(this.image, 'load', () => { this.classList.remove('is-loading'); this.querySelector('[data-error]').hidden = true; });
      this.on(this.image, 'error', () => { this.classList.remove('is-loading'); this.querySelector('[data-error]').hidden = false; });
      this.querySelectorAll('[data-enhanced]').forEach(node => { node.hidden = false; });
      this.contents.hidden = false;
      this.querySelector('[data-open-page]').disabled = false;
      this.readHash(true);
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.forEach(({ node, type, handler }) => node.removeEventListener(type, handler));
      this.closeZoom();
      this.restoreBodyOverflow();
      this.ready = false;
    }

    readHash(initial = false) {
      const match = location.hash.match(/^#catalog-pagina-(\d+)$/);
      const index = match ? Number(match[1]) - 1 : 0;
      this.setPage(index, false, initial);
    }

    setPage(index, updateUrl = false, force = false) {
      index = Math.min(this.pages.length - 1, Math.max(0, Number.isFinite(index) ? index : 0));
      if (!force && this.rendered && index === this.index) return;
      this.index = index;
      this.rendered = true;
      this.renderPage();
      if (updateUrl) {
        const url = new URL(location.href);
        url.hash = `catalog-pagina-${index + 1}`;
        history.pushState(null, '', url);
      }
    }

    renderPage(retry = false) {
      const page = this.pages[this.index];
      const title = page.title || `Pagina ${this.index + 1}`;
      this.classList.add('is-loading');
      this.querySelector('[data-error]').hidden = true;
      this.image.alt = `${title} — pagina ${this.index + 1} din ${this.pages.length}, catalog ItalFlex`;
      this.querySelector('[data-open-page]').setAttribute('aria-label', `Mărește ${title}, pagina ${this.index + 1}`);
      if (retry) this.image.removeAttribute('src');
      this.image.src = page.src;
      if (this.image.complete && this.image.naturalWidth) this.classList.remove('is-loading');
      this.select.value = String(this.index);
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.pages.length - 1;
      this.querySelector('[data-counter]').textContent = `${this.index + 1} / ${this.pages.length}`;
      this.querySelector('[data-status]').textContent = `Pagina ${this.index + 1} din ${this.pages.length}: ${title}.`;
      this.updateCurrentThumbnail();
      [this.index - 1, this.index + 1].forEach(index => {
        const adjacent = this.pages[index];
        if (!adjacent || this.preloaded.has(adjacent.src)) return;
        this.preloaded.add(adjacent.src);
        const image = new Image();
        image.decoding = 'async';
        image.src = adjacent.src;
      });
    }

    buildContents() {
      if (this.contentsBuilt) return;
      this.contentsBuilt = true;
      this.pages.forEach((page, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'if-catalog__thumbnail';
        button.dataset.page = String(index);
        button.setAttribute('aria-label', `Pagina ${index + 1}: ${page.title || 'Catalog'}`);
        if (page.thumbnail) {
          const image = document.createElement('img');
          image.src = page.thumbnail;
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.width = 260;
          image.height = 184;
          button.append(image);
        }
        const label = document.createElement('span');
        label.textContent = `${index + 1}. ${page.title || 'Catalog'}`;
        button.append(label);
        this.on(button, 'click', () => this.setPage(index, true));
        item.append(button);
        this.thumbnails.append(item);
      });
      this.updateCurrentThumbnail();
    }

    updateCurrentThumbnail() {
      this.thumbnails.querySelectorAll('[data-page]').forEach(button => {
        if (Number(button.dataset.page) === this.index) button.setAttribute('aria-current', 'page');
        else button.removeAttribute('aria-current');
      });
    }

    openZoom(trigger) {
      if (!this.ready || this.dialog.hasAttribute('open')) return;
      this.zoomTrigger = trigger;
      const page = this.pages[this.index];
      this.querySelector('[data-zoom-title]').textContent = `${page.title || 'Catalog'} · ${this.index + 1} / ${this.pages.length}`;
      this.zoomImage.src = page.src;
      this.zoomImage.alt = this.image.alt;
      this.zoom = window.innerWidth < 750 ? 2 : 1.5;
      this.setZoom(this.zoom, false);
      this.lockBodyOverflow();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      this.zoomPan.scrollLeft = 0;
      this.zoomPan.scrollTop = 0;
      this.querySelector('[data-zoom-close]').focus();
    }

    closeZoom() {
      if (!this.dialog || !this.dialog.hasAttribute('open')) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
      if (!this.classList.contains('is-expanded')) this.restoreBodyOverflow();
      if (this.zoomTrigger && this.zoomTrigger.isConnected) this.zoomTrigger.focus();
    }

    setZoom(value, keepCenter = true) {
      const x = (this.zoomPan.scrollLeft + this.zoomPan.clientWidth / 2) / (this.zoomPan.scrollWidth || 1);
      const y = (this.zoomPan.scrollTop + this.zoomPan.clientHeight / 2) / (this.zoomPan.scrollHeight || 1);
      this.zoom = Math.min(3, Math.max(1, value));
      this.zoomImage.style.width = `${this.zoom * 100}%`;
      this.querySelector('[data-zoom-value]').textContent = `${Math.round(this.zoom * 100)}%`;
      this.querySelector('[data-zoom-out]').disabled = this.zoom === 1;
      this.querySelector('[data-zoom-in]').disabled = this.zoom === 3;
      if (keepCenter) requestAnimationFrame(() => {
        this.zoomPan.scrollLeft = x * this.zoomPan.scrollWidth - this.zoomPan.clientWidth / 2;
        this.zoomPan.scrollTop = y * this.zoomPan.scrollHeight - this.zoomPan.clientHeight / 2;
      });
    }

    async toggleFullscreen() {
      if (document.fullscreenElement === this) { await document.exitFullscreen(); return; }
      if (this.classList.contains('is-expanded')) {
        this.classList.remove('is-expanded');
        this.restoreBodyOverflow();
        this.updateFullscreen();
        return;
      }
      if (typeof this.requestFullscreen === 'function') {
        try { await this.requestFullscreen(); return; } catch (_) { /* Safari fallback below. */ }
      }
      this.classList.add('is-expanded');
      this.lockBodyOverflow();
      this.updateFullscreen();
    }

    updateFullscreen() {
      const expanded = document.fullscreenElement === this || this.classList.contains('is-expanded');
      this.fullscreenButton.setAttribute('aria-pressed', String(expanded));
      this.fullscreenButton.setAttribute('aria-label', expanded ? 'Ieși din ecran complet' : 'Ecran complet');
      this.querySelector('[data-fullscreen-label]').textContent = expanded ? 'Ieși din ecran complet' : 'Ecran complet';
    }

    lockBodyOverflow() {
      if (this.previousBodyOverflow !== undefined) return;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    restoreBodyOverflow() {
      if (this.previousBodyOverflow === undefined) return;
      document.body.style.overflow = this.previousBodyOverflow;
      this.previousBodyOverflow = undefined;
    }

    onKeyDown(event) {
      if (this.dialog.hasAttribute('open')) {
        if (event.key === 'Escape') { event.preventDefault(); this.closeZoom(); }
        if (typeof this.dialog.showModal !== 'function' && event.key === 'Tab') this.trapFocus(event, this.dialog);
        return;
      }
      if (event.key === 'Escape' && this.classList.contains('is-expanded')) {
        event.preventDefault(); this.toggleFullscreen(); return;
      }
      if (this.classList.contains('is-expanded') && event.key === 'Tab') this.trapFocus(event, this);
      const target = event.target;
      if (target instanceof Element && target.closest('input,select,textarea,[contenteditable="true"]')) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.setPage(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
      }
    }

    trapFocus(event, scope) {
      const focusable = Array.from(scope.querySelectorAll('button:not([disabled]),a[href],select,[tabindex="0"],summary')).filter(node => node.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }

  customElements.define('italflex-catalog-reader', ItalFlexCatalogReader);
})();
